/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from "react";
import { Link } from "react-router-dom";
import { Button, Image, Container, Row, Col, Card } from "react-bootstrap";
import * as S from "./styles";

export interface IMainSectionItem {
  position: "left" | "right";
  title?: string;
  subtitle?: string;
  description?: string | string[];
  darkButton?: {
    title: string;
    icon?: string | any;
    link: string;
  };
  lightButton?: {
    title: string;
    icon?: string | any;
    link: string;
  };
  image: {
    name: string;
    link: string;
  };
}

export interface IMainSectionProps {
  mainSectionId?: string;
  mainSectionItems?: IMainSectionItem[];
  secondaryColor: string;
}

const isValidUrl = (urlString) => {
  try {
    return Boolean(new URL(urlString));
  } catch (e) {
    return false;
  }
};

export const MainSection = ({
  mainSectionId,
  mainSectionItems,
  secondaryColor,
}: IMainSectionProps) => {
  const _getIcon = (Icon: any) => {
    let _icon: any = [];
    if (typeof Icon === "string") {
      if (isValidUrl(Icon)) {
        _icon.push(
          <Image
            key={`image-icon`}
            src={Icon}
            width={60}
            height={60}
            className="icon icon-xs me-2"
          />
        );
      } else {
        const HeroIcon = require("@heroicons/react/solid");
        const NavIcon = HeroIcon[Icon];
        if (NavIcon) {
          _icon.push(
            <NavIcon
              key={`hero-icon`}
              src={Icon}
              className="icon icon-xs me-2"
            />
          );
        }
      }
      return _icon;
    }
    if (Icon) {
      _icon.push(<Icon key={`hero-icon-2`} className="icon icon-xs me-2" />);
    }
    return _icon;
  };

  const _getImagePositionStyle = (position: "left" | "right") => {
    let style: any = {};
    if (position === "left") {
      style.className = "order-lg-1";
    }
    return style;
  };

  const _getContentPositionStyle = (position: "left" | "right") => {
    let style: any = {};
    if (position === "left") {
      style.className = "order-lg-2 mb-5 mb-lg-0";
    }
    return style;
  };
  const _renderDescriptionIf = (item: IMainSectionItem) => {
    if (item?.description) {
      if (typeof item?.description === "string") {
        return (
          <S.Description className="mb-3">{item?.description}</S.Description>
        );
      }
      if (Array.isArray(item?.description) && item?.description?.length) {
        let _res: any = [];
        item?.description?.forEach((_desc: string, index: number) => {
          _res.push(
            <S.Description
              className="mb-3"
              key={`admin-landing-page-main-section-desc-${index}`}
            >
              {_desc}
            </S.Description>
          );
        });
        return _res;
      }
    }
  };

  const _renderBlockQuote = (item: any) => {
    if (item?.blockQuote) {
      return (
        <S.BockQuote className="blockquote" secondaryColor={secondaryColor}>
          <h4 className="mb-3 lead fw-bold">{item?.blockQuote?.title}</h4>
          <p className="mb-2">{item?.blockQuote?.description}</p>
        </S.BockQuote>
      );
    }
  };
  return (
    <section className="section section-md bg-soft pt-lg-5" id={mainSectionId}>
      <Container>
        {mainSectionItems?.map((item, index) => {
          if (item) {
            return (
              <Row
                key={`admin-landing-page-item-${index}`}
                className="justify-content-between align-items-center mb-5 mb-lg-7"
              >
                <Col lg={5} {..._getContentPositionStyle(item.position)}>
                  {item?.title && <S.Title>{item.title}</S.Title>}
                  {item?.subtitle && (
                    <p className="mb-3 lead fw-bold">{item.subtitle}</p>
                  )}
                  {_renderDescriptionIf(item)}
                  {_renderBlockQuote(item)}
                  {item?.darkButton && (
                    <Button
                      as={Link}
                      to={item?.darkButton?.link}
                      className="me-3"
                      variant="secondary"
                      target="_blank"
                    >
                      {_getIcon(item?.darkButton?.icon)}{" "}
                      {item?.darkButton?.title}
                    </Button>
                  )}
                  {item?.lightButton && (
                    <Button
                      as={Link}
                      to={item?.lightButton?.link}
                      className="me-3"
                      variant="outline-primary"
                      target="_blank"
                    >
                      {_getIcon(item?.lightButton?.icon)}{" "}
                      {item?.lightButton?.title}
                    </Button>
                  )}
                </Col>
                {item?.image && (
                  <Col lg={6} {..._getImagePositionStyle(item.position)}>
                    <Card>
                      <Image src={item?.image?.link} alt={item?.image?.name} />
                    </Card>
                  </Col>
                )}
                {item?.video && (
                  <Col lg={6} {..._getImagePositionStyle(item.position)}>
                    <Card>
                      <video width="100%" height={"auto"} controls>
                        <source
                          src={item?.video?.link}
                          type={item?.video?.type}
                        />
                      </video>
                    </Card>
                    <Image src={item?.image?.link} alt={item?.image?.name} />
                  </Col>
                )}
              </Row>
            );
          }
        })}
      </Container>
    </section>
  );
};

MainSection.defaultProps = {
  mainSectionId: "features",
};
